import * as bootstrap from 'bootstrap';
import { EnableAddressAutocomplete } from 'smg/addressautocomplete';
import * as countries from 'smg/countries';
import { ShowError } from 'smg/errors';
import * as models from 'smg/customer-api/models';
import { Phone } from 'smg/phone';

import { State } from '../state';

// BillingAddressForm owns the billing-address fields inside a payment card.
// It syncs its internal values with the DOM, handles the "same as shipping"
// toggle, and returns an Address snapshot via get().
export class BillingAddressForm {
    $fullName: HTMLInputElement;
    $company: HTMLInputElement;
    $country: HTMLSelectElement;
    $addressLine1: HTMLInputElement;
    $addressLine2: HTMLInputElement;
    $city: HTMLInputElement;
    $state: HTMLSelectElement;
    $zipCode: HTMLInputElement;
    $email: HTMLInputElement;
    $phone: HTMLInputElement;

    $billingFields: HTMLFieldSetElement;
    $addrSameToggle?: HTMLInputElement;

    #state: State;
    #billingCollapse?: bootstrap.Collapse;
    #phoneIti: Phone;
    // Resolves once the country and state options are loaded.
    readonly ready: Promise<void>;

    fullName = '';
    company = '';
    country: models.Country | undefined;
    countryID: models.CountryID | undefined;
    addressLine1 = '';
    addressLine2 = '';
    city = '';
    state: models.State | undefined;
    stateID: models.StateID | undefined;
    zip = '';
    email = '';
    phone = '';

    showOrHide = (): void => {
        if (!this.$addrSameToggle) {
            return;
        }

        this.$billingFields.disabled = this.$addrSameToggle.checked;
        if (this.$addrSameToggle.checked) {
            this.#billingCollapse?.hide();
        } else {
            this.#billingCollapse?.show();
        }
    };

    // get returns the current address. When "same as shipping" is checked
    // it overlays the shipping option onto this form's fields first.
    get = (): models.Address => {
        const shippingAddress = this.#state.shippingOption?.address;
        if (this.$addrSameToggle?.checked && shippingAddress) {
            this.fullName = shippingAddress.fullName ?? '';
            this.company = shippingAddress.organization ?? '';
            this.addressLine1 = shippingAddress.addressLine1 ?? '';
            this.addressLine2 = shippingAddress.addressLine2 ?? '';
            this.city = shippingAddress.city ?? '';
            this.zip = shippingAddress.postalCode ?? '';
            this.email = shippingAddress.emailAddress?.email ?? '';
            this.phone = shippingAddress.phoneNumber?.phone ?? '';
            this.country = shippingAddress.country;
            this.state = shippingAddress.state;
            this.countryID = shippingAddress.country?.id || shippingAddress.countryID || undefined;
            this.stateID = shippingAddress.state?.id || shippingAddress.stateID || undefined;
        }
        return new models.Address({
            fullName: this.fullName,
            organization: this.company,
            addressLine1: this.addressLine1,
            addressLine2: this.addressLine2,
            city: this.city,
            postalCode: this.zip,
            emailAddress: this.email ? { email: this.email } : undefined,
            phoneNumber: this.phone ? { phone: this.phone } : undefined,
            countryID: this.countryID,
            country: this.country,
            stateID: this.stateID,
            state: this.state,
        });
    };

    set = (addressInit: models.IAddress): void => {
        const address = new models.Address(addressInit);
        this.fullName = address.fullName || '';
        this.company = address.organization || '';
        this.addressLine1 = address.addressLine1 || '';
        this.addressLine2 = address.addressLine2 || '';
        this.city = address.city || '';
        this.zip = address.postalCode || '';
        this.email = address.emailAddress?.email || '';
        this.phone = address.phoneNumber?.phone || '';
        this.country = address.country;
        this.countryID = address.country?.id || address.countryID || undefined;
        this.state = address.state;
        this.stateID = address.state?.id || address.stateID || undefined;

        this.$fullName.value = this.fullName;
        this.$company.value = this.company;
        this.$addressLine1.value = this.addressLine1;
        this.$addressLine2.value = this.addressLine2;
        this.$city.value = this.city;
        this.$zipCode.value = this.zip;
        this.$email.value = this.email;

        // The country and state options exist only once Selects has loaded them.
        const { countryID, stateID } = this;
        void this.ready.then(() => {
            if (countryID) {
                this.$country.value = countryID.toString();
                this.$country.dispatchEvent(new Event('change'));
            }
            if (stateID) {
                this.$state.value = stateID.toString();
            }
        });

        setTimeout(async () => {
            await this.#phoneIti.iti.promise.then(() => this.#phoneIti.iti.setNumber(this.phone));
        });
    };

    constructor(state: State, $el?: HTMLElement) {
        this.#state = state;
        const shippingAddress = state.shippingOption?.address;
        const $scope = $el || state.dom.$paymentBody;
        this.$billingFields = $scope?.querySelector<HTMLFieldSetElement>('.billing-address-form') as HTMLFieldSetElement;
        this.$addrSameToggle = $scope?.querySelector<HTMLInputElement>('.billing-shipping-same') ?? undefined;

        if (this.$addrSameToggle) {
            this.#billingCollapse = new bootstrap.Collapse(this.$billingFields as HTMLElement, { toggle: false });
            this.showOrHide();
            this.$addrSameToggle.addEventListener('click', this.showOrHide);
        }

        this.$fullName = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-full-name') as HTMLInputElement;
        this.$company = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-company') as HTMLInputElement;
        this.$country = this.$billingFields?.querySelector<HTMLSelectElement>('.new-billing-address-country') as HTMLSelectElement;
        this.$addressLine1 = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-line-1') as HTMLInputElement;
        this.$addressLine2 = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-line-2') as HTMLInputElement;
        this.$city = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-city') as HTMLInputElement;
        this.$state = this.$billingFields?.querySelector<HTMLSelectElement>('.new-billing-address-state') as HTMLSelectElement;
        this.$zipCode = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-zip') as HTMLInputElement;
        this.$email = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-email') as HTMLInputElement;
        this.$phone = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-phone') as HTMLInputElement;

        this.#phoneIti = new Phone(this.$phone);

        this.ready = countries.Selects(this.$country, this.$state, {
            defaultUnitedStates: true,
            stateParentSelector: 'div',
            phone: this.#phoneIti,
            onStateHide: () => this.$zipCode.parentElement?.classList.replace('col-4', 'col-6'),
            onStateShow: () => this.$zipCode.parentElement?.classList.replace('col-6', 'col-4'),
        }).catch(ShowError);

        EnableAddressAutocomplete(this.$addressLine1, {
            city: this.$city,
            state: this.$state,
            zip: this.$zipCode,
            country: this.$country,
        });

        if (shippingAddress) {
            this.set({
                fullName: shippingAddress.fullName,
                organization: shippingAddress.organization,
                addressLine1: shippingAddress.addressLine1,
                addressLine2: shippingAddress.addressLine2,
                city: shippingAddress.city,
                postalCode: shippingAddress.postalCode,
                emailAddress: shippingAddress.emailAddress?.email ? { email: shippingAddress.emailAddress.email } : undefined,
                phoneNumber: shippingAddress.phoneNumber?.phone ? { phone: shippingAddress.phoneNumber.phone } : undefined,
                countryID: shippingAddress.countryID || undefined,
                stateID: shippingAddress.stateID || undefined,
            });
        }

        this.$fullName.addEventListener('input', () => {
            this.fullName = this.$fullName.value;
        });
        this.$company.addEventListener('input', () => {
            this.company = this.$company.value;
        });
        this.$addressLine1.addEventListener('input', () => {
            this.addressLine1 = this.$addressLine1.value;
        });
        this.$addressLine2.addEventListener('input', () => {
            this.addressLine2 = this.$addressLine2.value;
        });
        this.$city.addEventListener('input', () => {
            this.city = this.$city.value;
        });
        this.$zipCode.addEventListener('input', () => {
            this.zip = this.$zipCode.value;
        });
        this.$email.addEventListener('input', () => {
            this.email = this.$email.value;
        });
        this.$phone.addEventListener('input', () => {
            // getNumber() throws synchronously when intl-tel-input's utils.js
            // hasn't finished loading. Swallow it per-keystroke and let the
            // value re-sync once utils resolves; submit reads phone off the
            // billing form's own state, not this live getter.
            try {
                this.phone = this.#phoneIti.iti.getNumber();
            } catch {
                this.phone = this.$phone.value;
            }
        });

        this.$country.addEventListener('change', async () => {
            this.country = await countries.GetCountry(parseInt(this.$country.value, 10));
            this.countryID = this.country?.id;
            this.state = await countries.GetState(parseInt(this.$country.value, 10), parseInt(this.$state.value, 10));
            this.stateID = this.state?.id;
        });

        this.$state.addEventListener('change', async () => {
            this.state = await countries.GetState(parseInt(this.$country.value, 10), parseInt(this.$state.value, 10));
            this.stateID = this.state?.id;
        });
    }
}
